import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/hooks';
import { TransitControlFilter } from '../models';

const TRANSIT_CONTROL_FILTERS_STORAGE_KEY = 'BUSINESS-TRANSIT-CONTROL-FILTERS';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseCalendarDayFromUrl = (raw: string, boundary: 'start' | 'end'): Date | null => {
  if (DATE_ONLY_REGEX.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (boundary === 'end') date.setHours(23, 59, 59, 999);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseIsoDateFromJson = (value: unknown): Date | null => {
  if (value == null || value === '') return null;
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getEmptyTransitControlFilter = (): TransitControlFilter => ({
  originStatusId: '',
  destinationStatusId: '',
  dateFrom: null,
  dateTo: null,
  commerceId: '',
  logisticOperatorId: '',
  serviceWithOperatorIds: [],
});

const resolveInitialTransitControlFilter = (
  emptyFilterTemplate: TransitControlFilter,
  locationSearch: string,
  readQueryParam: (key: string) => string | null
): TransitControlFilter => {
  const storageJson = localStorage.getItem(TRANSIT_CONTROL_FILTERS_STORAGE_KEY);
  let filterAfterStorage: TransitControlFilter = emptyFilterTemplate;

  if (storageJson) {
    try {
      const parsed = JSON.parse(storageJson) as Record<string, unknown>;
      filterAfterStorage = {
        ...emptyFilterTemplate,
        originStatusId:
          typeof parsed.originStatusId === 'string' ? parsed.originStatusId : '',
        destinationStatusId:
          typeof parsed.destinationStatusId === 'string' ? parsed.destinationStatusId : '',
        commerceId: typeof parsed.commerceId === 'string' ? parsed.commerceId : '',
        logisticOperatorId:
          typeof parsed.logisticOperatorId === 'string' ? parsed.logisticOperatorId : '',
        serviceWithOperatorIds: Array.isArray(parsed.serviceWithOperatorIds)
          ? (parsed.serviceWithOperatorIds as string[])
          : [],
        dateFrom: parseIsoDateFromJson(parsed.dateFrom),
        dateTo: parseIsoDateFromJson(parsed.dateTo),
      };
    } catch {
      filterAfterStorage = emptyFilterTemplate;
    }
  }

  const urlParams = new URLSearchParams(locationSearch);
  const filterAfterUrl = { ...filterAfterStorage };

  for (const key of ['originStatusId', 'destinationStatusId', 'commerceId', 'logisticOperatorId'] as const) {
    if (!urlParams.has(key)) continue;
    filterAfterUrl[key] = readQueryParam(key) ?? '';
  }

  if (urlParams.has('serviceWithOperatorIds')) {
    const fromUrl = readQueryParam('serviceWithOperatorIds');
    filterAfterUrl.serviceWithOperatorIds = fromUrl ? fromUrl.split(',').filter(Boolean) : [];
  }

  if (urlParams.has('dateFrom')) {
    const fromUrl = readQueryParam('dateFrom');
    filterAfterUrl.dateFrom = fromUrl ? parseCalendarDayFromUrl(fromUrl, 'start') : null;
  }
  if (urlParams.has('dateTo')) {
    const fromUrl = readQueryParam('dateTo');
    filterAfterUrl.dateTo = fromUrl ? parseCalendarDayFromUrl(fromUrl, 'end') : null;
  }

  return filterAfterUrl;
};

const saveTransitControlFilterToLocalStorage = (filter: TransitControlFilter) => {
  try {
    localStorage.setItem(
      TRANSIT_CONTROL_FILTERS_STORAGE_KEY,
      JSON.stringify({
        originStatusId: filter.originStatusId,
        destinationStatusId: filter.destinationStatusId,
        commerceId: filter.commerceId,
        logisticOperatorId: filter.logisticOperatorId,
        serviceWithOperatorIds: filter.serviceWithOperatorIds,
        dateFrom: filter.dateFrom?.toISOString() ?? null,
        dateTo: filter.dateTo?.toISOString() ?? null,
      })
    );
  } catch {
    // localStorage puede no estar disponible (modo privado); no es crítico.
  }
};

const formatFilterDateForUrl = (date: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildUrlQueryFromTransitControlFilter = (filter: TransitControlFilter) => ({
  originStatusId: filter.originStatusId || undefined,
  destinationStatusId: filter.destinationStatusId || undefined,
  commerceId: filter.commerceId || undefined,
  logisticOperatorId: filter.logisticOperatorId || undefined,
  serviceWithOperatorIds: filter.serviceWithOperatorIds?.join(',') || undefined,
  dateFrom: formatFilterDateForUrl(filter.dateFrom),
  dateTo: formatFilterDateForUrl(filter.dateTo),
  page: '1',
});

const useTransitControlFilter = () => {
  const location = useLocation();
  const { getQueryParam, setQueryParams } = useQueryParams();

  const initialFilter = useMemo(() => getEmptyTransitControlFilter(), []);

  const [filter, setFilter] = useState<TransitControlFilter>(() =>
    resolveInitialTransitControlFilter(initialFilter, location.search, getQueryParam)
  );

  useEffect(() => {
    saveTransitControlFilterToLocalStorage(filter);
    setQueryParams(buildUrlQueryFromTransitControlFilter(filter));
  }, [filter]);

  return { filter, initialFilter, setFilter };
};

export default useTransitControlFilter;
